/**
 * Embed context — host↔shell postMessage renewal protocol (EMB-10)
 *
 * The embed shell has no password to fall back on, so a session-expiry
 * bounces silently to the host page (ControlJus) rather than to a login
 * form: on `auth:expired` (dispatched by apiFetch, lib/api.js) it asks the
 * host for a fresh token and waits a bounded time before giving up.
 *
 * Protocol (context.md decision 6):
 *   shell  → { type: "twc:embed:token-expired" }        → window.parent
 *   host   → { type: "twc:embed:token", token, expiresAt } → window (message event)
 * Both sides verify the message's origin before trusting it; this side does
 * it via `allowedOrigins`.
 *
 * NOTE on `allowedOrigins`: the authoritative list is per-partner config
 * (`EmbedPartnerConfig.frameOrigins`) delivered at bootstrap — that read path
 * lands with white-labeling (T19). Until then this component takes whatever
 * list its caller passes; an empty list means every message is rejected
 * (fail closed), never "trust anything".
 */
import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { setEmbedToken } from "@/lib/embedSession";

const EmbedContext = createContext({ isEmbed: false, renewalFailed: false, requestRenewal: () => {} });

const TOKEN_EXPIRED_MESSAGE = "twc:embed:token-expired";
const TOKEN_MESSAGE = "twc:embed:token";

// Bounded wait for the host to answer — long enough for a normal round-trip,
// short enough that the user isn't left staring at a stuck screen.
export const RENEWAL_TIMEOUT_MS = 5000;

export function EmbedProvider({ children, isEmbed = false, allowedOrigins = [], onToken }) {
  const [renewalFailed, setRenewalFailed] = useState(false);
  const pendingTimeoutRef = useRef(null);

  const isAllowedOrigin = useCallback(
    (origin) => allowedOrigins.includes(origin),
    [allowedOrigins],
  );

  const requestRenewal = useCallback(() => {
    if (!isEmbed) return;
    if (pendingTimeoutRef.current) return; // uma renovação por vez — não empilha timers

    // Um postMessage por origem autorizada, em vez de targetOrigin "*".
    // A origem exata do host não é conhecida de antemão, mas o conjunto de
    // origens POSSÍVEIS é — é o mesmo `allowedOrigins` que já filtra a
    // entrada. Para as origens que não são a do parent real, o browser
    // descarta a mensagem em silêncio, então na prática ela é entregue uma
    // única vez, e nunca a uma página que não esteja no allowlist do
    // parceiro. Com "*" qualquer página que nos enquadrasse receberia o
    // aviso (S2819): não viaja segredo nele, mas também não há razão para
    // contar a quem não deveria estar nos enquadrando.
    for (const origin of allowedOrigins) {
      window.parent.postMessage({ type: TOKEN_EXPIRED_MESSAGE }, origin);
    }

    pendingTimeoutRef.current = setTimeout(() => {
      pendingTimeoutRef.current = null;
      setRenewalFailed(true);
    }, RENEWAL_TIMEOUT_MS);
  }, [isEmbed, allowedOrigins]);

  useEffect(() => {
    if (!isEmbed) return undefined;
    window.addEventListener("auth:expired", requestRenewal);
    return () => window.removeEventListener("auth:expired", requestRenewal);
  }, [isEmbed, requestRenewal]);

  useEffect(() => {
    if (!isEmbed) return undefined;

    function handleMessage(event) {
      if (!isAllowedOrigin(event.origin)) {
        console.warn("[Embed] Ignoring postMessage from non-allowlisted origin:", event.origin);
        return;
      }

      const data = event.data;
      if (!data || typeof data !== "object" || data.type !== TOKEN_MESSAGE) return;
      if (!data.token) return; // malformado — ignora e deixa o timeout correr

      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }

      // Retoma na tela atual: só troca o token em memória, nenhum remount /
      // navegação acontece aqui — quem já está montado simplesmente volta a
      // ter um token válido na próxima chamada de apiFetch.
      setEmbedToken(data.token, data.expiresAt ?? null);
      setRenewalFailed(false);
      // EmbedEntry (T17) usa isto para re-rodar checkAuth() — o PRIMEIRO
      // token do bootstrap chega pelo mesmo caminho de uma renovação
      // (nenhum token ainda "expirou" de fato, mas pedir um é a mesma coisa).
      onToken?.(data.token, data.expiresAt ?? null);
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isEmbed, isAllowedOrigin, onToken]);

  // Limpa o timer pendente se o provider desmontar no meio de uma renovação.
  useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
    };
  }, []);

  return (
    <EmbedContext.Provider value={{ isEmbed, renewalFailed, requestRenewal }}>
      {children}
    </EmbedContext.Provider>
  );
}

export function useEmbed() {
  return useContext(EmbedContext);
}
